<script lang="ts">
  import { onMount } from "svelte"
  import { ArrowRight } from "lucide-svelte"
  import { toast, formatDate } from "$app/utils"
  import Modal from "$app/components/Modal.svelte"
  import AlignmentTest from "./AlignmentTest.svelte"
  import PurchaseAlignmentTest from "./PurchaseAlignmentTest.svelte"
  import { appStore } from "$app/store.svelte"
  import { CirclePlus, ChevronLeft, ChevronRight } from "lucide-svelte"

  let props = $props()
  let {
    alignment_only_members,
    onupdate
  }: {
    alignment_only_members: any[]
    onupdate: (data: AlignmentTestType[]) => void
  } = props

  let alignment_tests: AlignmentTestType[] = $state(props.alignment_tests || [])

  let _users: UserType[] = Object.values(appStore.users).filter(
    (u: UserType) => !u.deleted && u.role !== "coach"
  )

  let alignment_image = new URL(
    `/images/alignment_test/alignment-icon.png`,
    import.meta.url
  ).href

  let selected_alignment_test = $state(null)

  async function purchased(detail) {
    openPurchaseModal = false

    let alignment_test = detail

    alignment_test = {
      ...alignment_test,
      invitable_users: [
        ..._users.map((u) => {
          return { ...u, selected: false }
        }),
        ...alignment_only_members.map((u) => {
          return { ...u, role: "alignment", selected: false }
        })
      ]
    }

    alignment_tests = [...alignment_tests, alignment_test]
    onupdate(alignment_tests)
    toast("Alignment Test Purchase Successful", "success")
  }

  let openPurchaseModal = $state(false)

  function purchaseAlignmentTest() {
    openPurchaseModal = true
  }

  function updateAlignmentTest(data) {
    let alignment_test = data
    let foundIndex = alignment_tests.findIndex((t) => t.id == alignment_test.id)
    alignment_tests[foundIndex] = alignment_test
    if (selected_alignment_test.id == alignment_test.id) {
      selected_alignment_test = alignment_test
    }
  }

  let hash = ""

  onMount(() => {
    alignment_tests = [
      ...alignment_tests.map((a) => {
        return {
          ...a,
          invitable_users: [
            ..._users.map((u) => {
              return { ...u, selected: false }
            }),
            ...alignment_only_members.map((u) => {
              return { ...u, role: "alignment", selected: false }
            })
          ]
        }
      })
    ]

    hash = window.location.hash.substr(1)
  })

  function statusColor(alignment_test) {
    if (alignment_test.status == "not_started") {
      return "bg-red-100 text-red-800 ring-red-600/20"
    } else if (alignment_test.status == "collecting_pre_alignment_responses") {
      return "bg-yellow-50 text-yellow-800 ring-yellow-600/20"
    } else if (alignment_test.status == "scoring_pre_test_responses") {
      return "bg-blue-50 text-blue-800 ring-blue-600/20"
    } else if (alignment_test.status == "pre_scoring_completed") {
      return "bg-green-50 text-green-800 ring-green-600/20"
    } else if (alignment_test.status == "collecting_post_alignment_responses") {
      return "bg-yellow-100 text-yellow-800 ring-yellow-600/20"
    } else if (alignment_test.status == "scoring_post_test_responses") {
      return "bg-blue-100 text-blue-800 ring-blue-600/20"
    } else if (alignment_test.status == "post_scoring_completed") {
      return "bg-green-100 text-green-800 ring-green-600/20"
    } else {
      return "bg-yellow-50 text-yellow-800 ring-yellow-600/20"
    }
  }
</script>

{#if selected_alignment_test}
  <div class="relative">
    <div
      class="grid grid-cols-3 md:py-4 bg-zinc-200 px-2 border border-gray-200"
    >
      <div class="col-span-2 flex items-center justify-start">
        <button
          class="py-1.5 px-2 bg-blue-50 hover:bg-blue-100 text-blue-700 mr-3 text-sm rounded flex items-center border border-blue-200 hover:shadow"
          onclick={() => (selected_alignment_test = null)}
          ><ChevronLeft class="w-5 h-5 flex-none text-blue-600" /> Back</button
        >
        <img src={alignment_image} class="w-8 h-8 mr-2" alt="Alignment" />
        <h3 class="text-xl lg:text-2xl font-bold text-zinc-900">
          Alignment Test - {formatDate(selected_alignment_test.created_at)}
        </h3>
      </div>

      <div class="flex justify-end items-center gap-x-4">
        <span
          class="inline-flex items-center rounded-md {statusColor(
            selected_alignment_test
          )} px-3 py-2 text-xs font-medium ring-1 ring-inset"
        >
          {selected_alignment_test.status_title}
        </span>
      </div>
    </div>
    <AlignmentTest
      alignment_test={selected_alignment_test}
      onupdate={updateAlignmentTest}
    />
  </div>
{:else}
  <div class="relative">
    <div
      class="flex justify-between items-center mb-6 border-b border-gray-200 md:py-3 md:px-3"
    >
      <h3 class="text-lg md:text-2xl font-semibold leading-6 text-gray-900">
        Your Alignment Tests
      </h3>

      <div>
        {#if appStore.current_user.role === "admin"}
          <button
            type="button"
            class="inline-flex items-center gap-x-1.5 rounded-md bg-blue-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
            onclick={purchaseAlignmentTest}
          >
            <CirclePlus class="h-5 w-5" />
            Purchase Alignment Test
          </button>
        {/if}
      </div>
    </div>

    <ul role="list" class="divide-y space-y-4 divide-gray-100 mt-8 w-full">
      {#each alignment_tests as alignment_test, count (alignment_test.id)}
        <li class="relative hover:bg-zinc-100 border border-gray-200 shadow-sm">
          <button
            class="flex items-center justify-between gap-x-6 px-4 py-5 sm:px-6 lg:px-8 cursor-pointer w-full"
            onclick={() => (selected_alignment_test = alignment_test)}
            tabindex="0"
          >
            <div class="flex min-w-0 gap-x-4 items-center">
              <img
                src={alignment_image}
                class="w-10 h-10 bg-zinc-100 rounded-lg shadow"
                alt="Alignment Test"
              />
              <div class="min-w-0 flex-auto">
                <p class="text-lg font-semibold leading-6 text-gray-900">
                  Alignment Test #{count + 1}
                </p>
                <p class="text-sm text-gray-500">
                  Created: {formatDate(alignment_test.created_at)}
                </p>
              </div>
            </div>
            <div class="flex shrink-0 items-center gap-x-4">
              <p class="mt-1 flex text-xs leading-5 text-gray-500">
                <span
                  class="inline-flex items-center rounded-md shadow px-3 py-2 text-sm font-medium {statusColor(
                    alignment_test
                  )} ring-1 ring-inset ring-yellow-400/20"
                >
                  {alignment_test.status_title}
                </span>
              </p>

              <ArrowRight class="w-8 h-8 flex-none text-gray-400" />
            </div>
          </button>
        </li>
      {:else}
        <li
          class="relative flex justify-center items-center gap-x-6 px-4 py-5 bg-amber-100 rounded sm:px-6 lg:px-8"
        >
          <p class="text-xl font-bold">You have no Alignment Tests</p>
        </li>
      {/each}
    </ul>
  </div>
{/if}

<Modal
  closeButton={true}
  closeOnEsc={true}
  large={true}
  closeOnOuterClick={true}
  bind:open={openPurchaseModal}
>
  <PurchaseAlignmentTest onpurchase={purchased} />
</Modal>
